import { useState, type FormEvent } from "react";

import type { CartCoupon } from "@/lib/api-client";
import { describeCartError, useApplyCoupon, useRemoveCoupon } from "@/lib/cart";
import { formatXOF } from "@/lib/format";

export interface CouponFormProps {
  coupon: CartCoupon | null;
}

/**
 * Code promo (§7.2) : « replié par défaut ; erreur explicite (expiré, minimum
 * non atteint, déjà utilisé) ». Porté depuis
 * apps/web/components/cart/CouponForm.tsx : `useActionState`/Server Action →
 * `useApplyCoupon()`/`useRemoveCoupon()` (TanStack Query, src/lib/cart.ts),
 * erreur affichée en ligne via un `useState` local plutôt que l'état de
 * l'action. `<details>` garde le même repli natif par défaut.
 */
export function CouponForm({ coupon }: CouponFormProps) {
  const applyCoupon = useApplyCoupon();
  const removeCoupon = useRemoveCoupon();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (coupon) {
    return (
      <div className="flex items-center justify-between gap-3 border border-line bg-cream2 px-3 py-2 text-sm">
        <div>
          <p className="font-semibold text-ink">Code « {coupon.code} »</p>
          <p className="text-xs text-muted">Remise : −{formatXOF(coupon.discount.amountMinor)}</p>
        </div>
        <button
          type="button"
          onClick={() => removeCoupon.mutate()}
          disabled={removeCoupon.isPending}
          className="text-xs font-medium text-navy underline hover:text-gold disabled:cursor-not-allowed disabled:opacity-60"
        >
          {removeCoupon.isPending ? "…" : "Retirer"}
        </button>
      </div>
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Merci de saisir un code promo.");
      return;
    }
    setError(null);
    applyCoupon.mutate(trimmed, {
      onSuccess: () => setCode(""),
      onError: (err) => setError(describeCartError(err)),
    });
  }

  return (
    <details className="group border border-line">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-navy">Code promo</summary>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 border-t border-line p-3">
        <div className="flex gap-2">
          <label htmlFor="coupon-code" className="sr-only">
            Code promo
          </label>
          <input
            id="coupon-code"
            name="code"
            type="text"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Votre code"
            className="w-full border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:outline-none"
          />
          <button
            type="submit"
            disabled={applyCoupon.isPending}
            className="shrink-0 border border-navy bg-navy px-4 py-2 text-xs font-medium uppercase tracking-wide text-cream hover:bg-navy2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {applyCoupon.isPending ? "…" : "Appliquer"}
          </button>
        </div>
        {error ? (
          <p role="alert" className="text-xs font-medium text-red-700">
            {error}
          </p>
        ) : null}
      </form>
    </details>
  );
}
